import { DonationType, DonorFormValue } from './donation-flow.models';
import {
  ActionType,
  Address,
  CustomerRequest,
  CustomerResponse,
  DonationMetadata,
} from './stripe-models';

function buildAddress(v: DonorFormValue): Address {
  const address: Address = {
    line1: v.line1,
    line2: v.line2 || undefined,
    city: v.city,
    state: v.state,
    postalCode: v.postalCode,
    country: v.country,
  };
  return address;
}

export function buildCustomerRequest(v: DonorFormValue): CustomerRequest {
  const customer: CustomerRequest = {
    action: 'Create' as ActionType,
    name: `${v.firstName} ${v.lastName}`,
    email: v.email,
    companyName: v.isCompany ? v.companyName : undefined,
    phone: v.phone,
    phoneCode: v.phoneCode,
    address: buildAddress(v),
  };

  return customer;
}

/**
 * Metadata that Stripe stores alongside every invoice/subscription line.
 * `productId` and `productFeeId` are always required: they drive fee/net
 * splitting on the backend. Missing either is a bug in step 1 or in the
 * campaign data, so we throw instead of silently sending a placeholder.
 */
export function buildDonationMetadata(
  v: DonorFormValue,
  productId: string,
  productFeeId: string,
  donationType: DonationType
): DonationMetadata {
  if (!productId || !productFeeId) {
    throw new Error(
      `[buildDonationMetadata] Missing required product identifiers ` +
        `(productId="${productId}", productFeeId="${productFeeId}"). ` +
        `The selected campaign must expose both.`
    );
  }

  const metadata: DonationMetadata = {
    customerName: `${v.firstName} ${v.lastName}`,
    companyName: v.companyName,
    customerEmail: v.email,
    productId,
    productFeeId,
    displayDonorName: !!v.displayName,
    donationType,
    source: 'web',
  };

  return metadata;
}

export function buildDonorFormValue(
  customer: Omit<CustomerResponse, 'object' | 'description' | 'created'> | null,
  metadata: DonationMetadata | null
): DonorFormValue {
  const nameParts = (customer?.name ?? '').trim().split(' ');
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ') ?? '';

  const address = customer?.address ?? metadata?.address ?? {};

  return {
    email: customer?.email ?? metadata?.customerEmail ?? '',
    firstName,
    lastName,
    // @ts-ignore
    displayName: metadata?.displayDonorName ?? false,
    phone: customer?.phone ?? '',
    phoneCode: customer?.phoneCode ?? '',
    isCompany: !!metadata?.companyName,
    companyName: metadata?.companyName ?? '',
    line1: address?.line1 ?? '',
    line2: address?.line2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    // @ts-ignore
    postalCode: address?.postalCode ?? address?.postal_code ?? '',
    country: address?.country ?? '',
  };
}
